import { WorkflowData } from "../WorkflowEditor";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";

interface SettingsTabProps {
  data: WorkflowData;
  onChange: (data: WorkflowData) => void;
}

export function SettingsTab({ data, onChange }: SettingsTabProps) {
  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold">Workflow Settings</h2>
        <p className="text-sm text-muted-foreground">
          Configure workflow behavior and visibility
        </p>
      </div>

      <Card className="p-4 space-y-4">
        <h3 className="font-medium">Visibility & Status</h3>
        
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="is_active">Active</Label>
            <p className="text-xs text-muted-foreground">
              Make this workflow visible to users
            </p>
          </div>
          <Switch
            id="is_active"
            checked={data.is_active}
            onCheckedChange={(checked) => onChange({ ...data, is_active: checked })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="sort_order">Sort Order</Label>
          <Input
            id="sort_order"
            type="number"
            value={data.sort_order}
            onChange={(e) => onChange({ ...data, sort_order: parseInt(e.target.value) || 0 })}
            placeholder="0"
          />
          <p className="text-xs text-muted-foreground">
            Lower numbers appear first in the workflow library
          </p>
        </div>
      </Card>

      <Card className="p-4 space-y-4">
        <h3 className="font-medium">Version Control</h3>
        
        <div className="space-y-2">
          <Label>Current Version</Label>
          <Input value={`Version ${data.version}`} disabled />
        </div>

        <div className="space-y-2">
          <Label htmlFor="change_notes">Change Notes</Label>
          <Textarea
            id="change_notes"
            value={data.change_notes}
            onChange={(e) => onChange({ ...data, change_notes: e.target.value })}
            placeholder="Describe what changed in this version..."
            rows={4}
          />
          <p className="text-xs text-muted-foreground">
            These notes will be saved when you update the workflow
          </p>
        </div>
      </Card>

      <Card className="p-4 space-y-4">
        <h3 className="font-medium">Feature Flags</h3>
        
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Test Mode</Label>
            <p className="text-xs text-muted-foreground">
              Only visible to admins for testing
            </p>
          </div>
          <Switch
            checked={data.feature_flags?.test_mode || false}
            onCheckedChange={(checked) => onChange({ 
              ...data, 
              feature_flags: { ...data.feature_flags, test_mode: checked }
            })}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Beta Feature</Label>
            <p className="text-xs text-muted-foreground">
              Show "Beta" badge to users
            </p>
          </div>
          <Switch
            checked={data.feature_flags?.beta || false}
            onCheckedChange={(checked) => onChange({ 
              ...data, 
              feature_flags: { ...data.feature_flags, beta: checked }
            })}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Track Analytics</Label>
            <p className="text-xs text-muted-foreground">
              Enable detailed usage analytics
            </p>
          </div>
          <Switch
            checked={data.feature_flags?.track_analytics !== false}
            onCheckedChange={(checked) => onChange({ 
              ...data, 
              feature_flags: { ...data.feature_flags, track_analytics: checked }
            })}
          />
        </div>
      </Card>

      <Card className="p-4 space-y-4">
        <h3 className="font-medium">Testing</h3>
        
        <div className="space-y-2">
          <Label>Test Status</Label>
          <Input value="Not tested" disabled />
        </div>

        <div className="space-y-2">
          <Label>Last Tested</Label>
          <Input value="Never" disabled />
        </div>
      </Card>
    </div>
  );
}